<template>
	<BlogRoot
		v-if="!isLoading"
		:posts="list"
		:current-page="Number(pageNumber)"
		:previous-page="Number(previousPageNumber)"
		:total-pages="Number(total_pages)"
	/>
	<div
		v-else
		class="h-[600px]"
	/>
	<Head>
		<Title>{{ yoast_head_json?.title }}</Title>
		<Link
			rel="canonical"
			:href="current_hostname + 'blog/'"
		/>

		<template v-if="head_seo">
			<template
				v-for="(tag, i) in head_seo"
				:key="i"
			>
				<Meta
					v-if="tag[0].startsWith('og_')"
					:property="'og:' + tag[0].slice(3)"
					:content="tag[1][0].url || tag[1]"
				/>

				<Meta
					v-else-if="tag[0].startsWith('article')"
					:property="'article:' + tag[0].slice(8)"
					:content="tag[1]"
				/>
				<Meta
					v-else-if="tag[0] !== 'twitter_misc' && tag[0].startsWith('twitter')"
					:name="'twitter:' + tag[0].slice(8)"
					:content="tag[1]"
				/>

				<template v-else-if="tag[0] === 'twitter_misc'">
					<Meta
						:name="'twitter:label1'"
						:content="Object.keys(tag[1])[0]"
					/>
					<Meta
						:name="'twitter:data2'"
						:content="Object.values(tag[1])[0]"
					/>
					<Meta
						:name="'twitter:label2'"
						:content="Object.keys(tag[1])[1]"
					/>
					<Meta
						:name="'twitter:data1'"
						:content="Object.values(tag[1])[1]"
					/>
				</template>

				<Meta
					v-else-if="tag[0] != 'og_image'"
					:name="tag[0]"
					:content="tag[1]"
				/>
				<template v-else>
					<Meta
						:property="tag[0].split('_').join(':') + ':type'"
						:content="tag[1][0].type"
					/>
					<Meta
						:property="tag[0].split('_').join(':')"
						:content="tag[1][0].url"
					/>
					<Meta
						:property="tag[0].split('_').join(':') + ':width'"
						:content="tag[1][0].width"
					/>
					<Meta
						:property="tag[0].split('_').join(':') + ':height'"
						:content="tag[1][0].height"
					/>
				</template>
			</template>
		</template>
	</Head>
</template>

<script setup>
import axios from "axios";
const $route = useRoute();
const $route_name = ref("");

const hostname = useRuntimeConfig().public.hostname;
const { current_hostname } = useRuntimeConfig().public;

const blogList = useBlogListFetch();
const isLoading = ref(true);
const head_seo = computed(() => {
	if (yoast_head_json) {
		const filtered = Object.entries(yoast_head_json).filter(
			(item) =>
				![
					"title",
					"robots",
					"canonical",
					"schema_json",
					"WordPressAuthor_YoastHeadJson_Schema_"
				].includes(item[0])
		);
		return filtered;
	}

	return null;
});

const { data } = await useAsyncData("page_seo_data", async ({ $config }) => {
	const { data } = await axios.get(`${hostname}wp-json/wp/v2/pages/9014/`);

	const { yoast_head_json, yoast_meta } = data;
	const { schema_json } = data.yoast_head_json;

	let parsed_schema;
	if (schema_json) {
		parsed_schema = JSON.parse(schema_json);
		const parsed_schema_id = parse_saswpSchema(
			parsed_schema["@graph"],
			$config.public.hostname,
			$config.public.current_hostname
		);

		parsed_schema["@graph"] = parsed_schema_id;
	}
	const result = await blogList;

	if (result.error) {
		return null;
	}

	return {
		yoast_head_json,
		yoast_meta,
		schema_json: parsed_schema,
		...result
	};
});

if (!data.value) {
	throw createError({
		statusCode: 404,
		statusMessage: "Page Not Found",
		fatal: true
	});
}

const {
	yoast_head_json,
	schema_json,
	list,
	pageNumber,
	previousPageNumber,
	total_pages
} = data.value;

onMounted(async () => {
	$route_name.value = $route.name;

	if (list) {
		isLoading.value = false;
	}

	if (list && process.client) {
		await setTimeout(() => window.scrollTo(0, 1), 1000);
	}
});

useHead({
	script: [
		{
			type: "application/ld+json",
			class: "saswp-schema-markup-output",
			innerHTML: JSON.stringify(schema_json)
		}
	]
});
</script>
